/**
 * What a session may do with S1–S4, given its track (owner ruling 2026-07-27,
 * src/lib/sessionStepInheritance.ts). One read for every session screen, the same
 * rows iPhone reads (DB.trackLocks). The pool lock comes from item_pools, not its
 * copy on session_baselines. The server holds these locks whatever a screen does
 * (migration 1031); this is what lets a screen say so before anyone clicks.
 *
 * `steps` is null until read, and stays null when the read fails: that says
 * nothing, rather than claiming a step is not set on the track.
 *
 * Weights (migration 1048): `trackWeights` is what a session on the track scores
 * with when the track sets them, and `weightsLocked` says the server holds every
 * session on the track to them (the criteria lock). `readTrackLocks` is exported
 * for the Event Designer's store, which needs the same answer outside React.
 */
import { useEffect, useState } from 'react';
import { getTypedSupabaseClient } from '../lib/typedSupabaseClient';
import {
  resolveStandaloneSteps,
  resolveTrackSteps,
  type PrelimStepId,
  type StepResolution,
  type TrackLockState,
} from '../lib/sessionStepInheritance';
import { trackScoringScale } from '../lib/trackScoringScale';
import { trackWeightShares } from '../lib/trackCriteriaWeights';
import type { ScoringScaleRange } from '../lib/toolSettings';

export type TrackSteps = Record<PrelimStepId, StepResolution>;

export interface TrackLockRead {
  locks: TrackLockState;
  trackScale: ScoringScaleRange | null;
  /** The track's weights as shares of 1 by attribute key, or null when it sets none. */
  trackWeights: Record<string, number> | null;
}

export async function readTrackLocks(trackId: string): Promise<TrackLockRead | null> {
  const db = getTypedSupabaseClient();
  const [track, pool, criteria, people] = await Promise.all([
    db
      .from('tracks')
      .select('cycle_id, participation_locked_at, range_set_locked_at, session_baselines(criteria_locked_at, values_converged_at)')
      .eq('id', trackId)
      .maybeSingle(),
    db
      .from('item_pools')
      .select('pool_locked_at, priority_ids')
      .eq('track_id', trackId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    db.from('cycle_attribute_configs').select('range_value, attribute_key, weight, cycle_id, created_at').eq('track_id', trackId).order('created_at', { ascending: true }),
    db
      .from('track_participants')
      .select('id', { count: 'exact', head: true })
      .eq('track_id', trackId)
      .eq('status', 'active'),
  ]);
  if (track.error || !track.data) return null;

  const baseline = track.data.session_baselines;
  const trackCycleId = track.data.cycle_id ?? null;
  const criteriaRows = criteria.data ?? [];
  return {
    locks: {
      participationLockedAt: track.data.participation_locked_at,
      poolLockedAt: pool.data?.pool_locked_at ?? null,
      criteriaLockedAt: baseline?.criteria_locked_at ?? null,
      valuesConvergedAt: baseline?.values_converged_at ?? null,
      rangeSetLockedAt: track.data.range_set_locked_at,
      participantCount: people.count ?? 0,
      poolItemCount: pool.data?.priority_ids?.length ?? 0,
      criteriaCount: criteriaRows.length,
    },
    trackScale: trackScoringScale(criteriaRows.map((row) => row.range_value)),
    // The track's rows in its cycle, as the server reads them (1048).
    trackWeights: trackWeightShares(
      criteriaRows
        .filter((row) => (row.cycle_id ?? null) === trackCycleId)
        .map((row) => ({ attributeKey: row.attribute_key, weight: row.weight })),
    ),
  };
}

export interface TrackStepLocks {
  steps: TrackSteps | null;
  /** Value ranges are criteria content (canon 28 §2.2), with their own lock too. */
  rangesLocked: boolean;
  /** The one range a session on this track scores on, or null when the track sets none (S-39). */
  trackScale: ScoringScaleRange | null;
  /** The track's range lock alone: the one the server holds a session's range to (1038). */
  scaleLocked: boolean;
  /** The weights a session on this track scores with, as shares of 1, or null when the track sets none (1048). */
  trackWeights: Record<string, number> | null;
  /** The track's criteria are locked, and with them its weights: every session on it scores with them (1048). */
  weightsLocked: boolean;
}

const UNREAD: TrackStepLocks = {
  steps: null,
  rangesLocked: false,
  trackScale: null,
  scaleLocked: false,
  trackWeights: null,
  weightsLocked: false,
};

export function useTrackStepLocks(trackId: string | null | undefined): TrackStepLocks {
  const [state, setState] = useState<TrackStepLocks>(UNREAD);

  useEffect(() => {
    if (!trackId) {
      setState({ ...UNREAD, steps: resolveStandaloneSteps() });
      return;
    }
    let cancelled = false;
    void readTrackLocks(trackId).then((read) => {
      if (cancelled) return;
      setState(
        read
          ? {
              steps: resolveTrackSteps(read.locks),
              rangesLocked: read.locks.criteriaLockedAt !== null || read.locks.rangeSetLockedAt !== null,
              trackScale: read.trackScale,
              scaleLocked: read.locks.rangeSetLockedAt !== null,
              trackWeights: read.trackWeights,
              weightsLocked: read.locks.criteriaLockedAt !== null,
            }
          : UNREAD,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [trackId]);

  return state;
}
